import { type V1ApiResponse } from '@webapp/core/interfaces/api/V1ApiResponse'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPrimaryCategoryRepository } from '@webapp/platform/organization-management/business-logic/interfaces/IPrimaryCategoryRepository'
import { type PrimaryCategoryConfigConstructor } from '@webapp/platform/organization-management/entities/PrimaryCategoryConfig'
import { type PrimaryCategoryConfigDetailConstructor } from '@webapp/platform/organization-management/entities/PrimaryCategoryConfigDetail'
import { type PrimaryCategoryConfigUserConstructor } from '@webapp/platform/organization-management/entities/PrimaryCategoryConfigUser'
import { type SecondaryCategoryConfigConstructor } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import { mapPrimaryCategoryConfigApiModel } from '../../mappers/mapPrimaryCategoryConfigApiModel'
import { mapPrimaryCategoryConfigDetailApiModel } from '../../mappers/mapPrimaryCategoryConfigDetailApiModel'
import { mapPrimaryCategoryConfigUserApiModel } from '../../mappers/mapPrimaryCategoryConfigUserApiModel'
import { mapCreatePrimaryCategoryInputDataToPayload } from '../../mappers/mapPrimaryCategoryFormFieldsToCreatePayload'
import { mapUpdatePrimaryCategoryInputDataToPayload } from '../../mappers/mapPrimaryCategoryFormFieldsToUpdatePayload'
import { mapSecondaryCategoryConfigApiModel } from '../../mappers/mapSecondaryCategoryConfigApiModel'
import { mapCreateSecondaryCategoryInputDataToPayload } from '../../mappers/mapSecondaryCategoryFormFieldsToCreatePayload'
import { mapUpdateSecondaryCategoryInputDataToPayload } from '../../mappers/mapSecondaryCategoryFormFieldsToUpdatePayload'
import { type CreatePrimaryCategoryInputData } from '../../types/CreatePrimaryCategoryInputData'
import { type CreatePrimaryCategoryResponse } from '../../types/CreatePrimaryCategoryResponse'
import { type CreateSecondaryCategoryInputData } from '../../types/CreateSecondaryCategoryInputData'
import { type CreateSecondaryCategoryResponse } from '../../types/CreateSecondaryCategoryResponse'
import { type GetPrimaryCategoryConfigDetailResponse } from '../../types/GetPrimaryCategoryConfigDetailResponse'
import { type GetPrimaryCategoryConfigListResponse } from '../../types/GetPrimaryCategoryConfigListResponse'
import { type GetPrimaryCategoryConfigUserListResponse } from '../../types/GetPrimaryCategoryConfigUserListResponse'
import { type GetSecondaryCategoryConfigDetailResponse } from '../../types/GetSecondaryCategoryConfigDetailResponse'
import { type GetSecondaryCategoryConfigListResponse } from '../../types/GetSecondaryCategoryConfigListResponse'
import { type UpdatePrimaryCategoryInputData } from '../../types/UpdatePrimaryCategoryInputData'
import { type UpdateSecondaryCategoryInputData } from '../../types/UpdateSecondaryCategoryInputData'

interface Dependency {
  httpClient: IHttpClient
}

export class PrimaryCategoryRepository implements IPrimaryCategoryRepository {
  constructor(private readonly deps: Dependency) {}

  async listConfigs(
    params: RepoFilterParams & {
      active?: boolean
    }
  ): Promise<RepoPaginatedResponse<PrimaryCategoryConfigConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetPrimaryCategoryConfigListResponse>(
          `/api/v2/locations/`,
          {
            params: {
              active: params.active,
              is_editable: true,
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const configs = res.data.data
      const metadata = res.data.metadata

      return {
        data: configs.map((config) => mapPrimaryCategoryConfigApiModel(config)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getConfigById(
    id: number
  ): Promise<PrimaryCategoryConfigDetailConstructor | null> {
    try {
      const res =
        await this.deps.httpClient.get<GetPrimaryCategoryConfigDetailResponse>(
          `/api/v2/locations/${id}/`,
          {
            params: {
              is_editable: true,
            },
          }
        )

      const config = res.data.data

      if (!config) {
        return null
      }

      return mapPrimaryCategoryConfigDetailApiModel(config)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listSecondaryCategoryConfigs(
    primaryCategoryId: number,
    params: RepoFilterParams
  ): Promise<RepoPaginatedResponse<SecondaryCategoryConfigConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetSecondaryCategoryConfigListResponse>(
          `/api/v3/departments/`,
          {
            params: {
              branch: primaryCategoryId,
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const configs = res.data.data
      const metadata = res.data.metadata

      return {
        data: configs.map((config) =>
          mapSecondaryCategoryConfigApiModel(config)
        ),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getSecondaryCategoryConfigById(
    id: number
  ): Promise<SecondaryCategoryConfigConstructor | null> {
    try {
      const res =
        await this.deps.httpClient.get<GetSecondaryCategoryConfigDetailResponse>(
          `/api/v3/departments/${id}/`
        )

      const config = res.data.data

      if (!config) {
        return null
      }

      return mapSecondaryCategoryConfigApiModel(config)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listConfigUsers(
    primaryCategoryId: number,
    params: RepoFilterParams
  ): Promise<RepoPaginatedResponse<PrimaryCategoryConfigUserConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetPrimaryCategoryConfigUserListResponse>(
          `/api/v3/users/basic/`,
          {
            params: {
              location: primaryCategoryId,
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const users = res.data.data
      const metadata = res.data.metadata

      return {
        data: users.map((user) => mapPrimaryCategoryConfigUserApiModel(user)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async create(
    inputData: CreatePrimaryCategoryInputData
  ): Promise<{ id: number }> {
    try {
      const payload = mapCreatePrimaryCategoryInputDataToPayload(inputData)
      const res =
        await this.deps.httpClient.post<CreatePrimaryCategoryResponse>(
          `/api/v2/locations/`,
          payload
        )

      return { id: res.data.data.id }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(
    id: number,
    inputData: UpdatePrimaryCategoryInputData
  ): Promise<void> {
    try {
      const payload = mapUpdatePrimaryCategoryInputDataToPayload(inputData)
      await this.deps.httpClient.put(`/api/v2/locations/${id}/`, payload)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createSecondaryCategory(
    primaryCategoryId: number,
    inputData: CreateSecondaryCategoryInputData
  ): Promise<SecondaryCategoryConfigConstructor> {
    try {
      const payload = mapCreateSecondaryCategoryInputDataToPayload(inputData)
      const res =
        await this.deps.httpClient.post<CreateSecondaryCategoryResponse>(
          `/api/v3/departments/`,
          {
            ...payload,
            branch: primaryCategoryId,
          }
        )

      return mapSecondaryCategoryConfigApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateSecondaryCategory(
    secondaryCategoryId: number,
    inputData: UpdateSecondaryCategoryInputData
  ): Promise<void> {
    try {
      const payload = mapUpdateSecondaryCategoryInputDataToPayload(inputData)

      await this.deps.httpClient.put(
        `/api/v3/departments/${secondaryCategoryId}/`,
        payload
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteSecondaryCategory(secondaryCategoryId: number): Promise<void> {
    try {
      const res = await this.deps.httpClient.delete<
        V1ApiResponse<Record<string, never>>
      >(`/api/v2/department/${secondaryCategoryId}/`)

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message: res.data.description,
          status: 200,
          response: null,
        })
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async activate(id: number): Promise<void> {
    try {
      await this.deps.httpClient.patch(`/api/v2/locations/${id}/activate/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deactivate(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v2/locations/${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async setAsHeadquarters(id: number): Promise<void> {
    try {
      await this.deps.httpClient.patch(
        `/api/v2/locations/${id}/set_headquarter/`
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
